// Failures name the problem and the recovery (docs/cockpit/spec.md): what did not happen, and what the
// person or LAMP does next. Chat and the Cockpit show them the same way.
import { ApiError, type Failure } from "../api.js";
import { CATALOGUES, useWords, type Words } from "../i18n/words.js";

/** Problems as one sentence, ending in a full stop; undefined for none. */
const sentence = (joined: string) => (joined === "" ? undefined : /[.!?]$/.test(joined) ? joined : `${joined}.`);

/**
 * Why an action the person took failed, and how to recover, from the API's answer, in the words given
 * (English unless a catalogue is passed). Problems the API names stay as the API worded them.
 */
export function nameFailure(error: unknown, words: Words = CATALOGUES.en): Failure {
  if (!(error instanceof ApiError)) {
    return { problem: words.failureUnreachable, recovery: words.failureCheckConnection };
  }
  const joined = error.problems.join("; ");
  // What to do next, as the API said it: a Task LAMP can't record yet, or one that waits on another.
  if (error.recovery && error.status !== 401)
    return { problem: sentence(joined) ?? words.failureRefused(error.code), recovery: error.recovery };
  const named = sentence(joined);
  switch (error.status) {
    case 401:
      return { problem: words.failureSignedOut, recovery: words.failureSignIn };
    case 403:
      return { problem: named ?? words.failureForbidden, recovery: words.failureAskOwner };
    case 409:
      return { problem: named ?? words.failureChanged, recovery: words.failureCheckAgain };
    case 423:
      // A read-only business: the notice says why and what to do, so it is the one message (#784).
      return { problem: named ?? words.failureRefused(error.code), recovery: "" };
    default:
      return error.status >= 500
        ? { problem: named ?? words.failureNotFinished, recovery: words.failureCheckHappened }
        : { problem: named ?? words.failureRefused(error.code), recovery: words.failureChange };
  }
}

/** A failure as the person reads it: the problem, the recovery, and Retry now when there is one. */
export function FailureNotice({
  failure,
  busy = false,
  onRetry,
  id,
}: {
  failure: Failure;
  busy?: boolean;
  onRetry?: (() => void) | undefined;
  id?: string;
}) {
  const w = useWords();
  return (
    <div id={id} className="alert failure" role="alert">
      <p>{failure.problem}</p>
      {failure.recovery === "" ? null : <p className="recovery">{failure.recovery}</p>}
      {onRetry ? (
        <button type="button" className="btn" disabled={busy} onClick={onRetry}>
          {w.failureRetry}
        </button>
      ) : null}
    </div>
  );
}
